import React from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

/**
 * Sticky footer action bar shown when one or more items are selected.
 * Props:
 *   - count (number): how many items selected
 *   - onClear (fn): clear selection
 *   - actions: Array<{ key, label, variant?, Icon?, onClick, destructive? }>
 */
export default function BulkActionBar({ count, onClear, actions = [], testId = "bulk-action-bar" }) {
    if (count === 0) return null;
    return (
        <div
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-primary text-primary-foreground shadow-2xl rounded-full px-5 py-2 flex items-center gap-3 border border-primary/20 animate-in fade-in slide-in-from-bottom-4"
            data-testid={testId}
        >
            <button
                type="button"
                onClick={onClear}
                className="p-1 rounded-full hover:bg-primary-foreground/10 transition-colors"
                aria-label="Clear selection"
                data-testid={`${testId}-clear`}
            >
                <X className="h-4 w-4" />
            </button>
            <span className="text-sm">
                <span className="font-semibold" data-testid={`${testId}-count`}>
                    {count}
                </span>{" "}
                selected
            </span>
            <div className="h-5 w-px bg-primary-foreground/20 mx-1" />
            <div className="flex gap-2">
                {actions.map(({ key, label, variant, Icon, onClick, destructive }) => (
                    <Button
                        key={key}
                        size="sm"
                        variant={variant || "secondary"}
                        onClick={onClick}
                        className={
                            destructive
                                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                : "bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                        }
                        data-testid={`${testId}-action-${key}`}
                    >
                        {Icon && <Icon className="h-3.5 w-3.5 mr-1.5" />}
                        {label}
                    </Button>
                ))}
            </div>
        </div>
    );
}
